/**
 * Hook pour s'abonner aux jobs Sora via Firebase RTDB (temps réel).
 * Avec fallback API si RTDB échoue.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getMyJobs } from '../services/soraService';

/**
 * Convertit un objet RTDB en array de jobs triés par date
 */
function parseJobsFromRTDB(data) {
  if (!data) return [];
  
  // RTDB peut retourner un objet ou un array
  const jobs = Object.values(data);
  
  // Trier par created_at décroissant (plus récent en premier)
  return jobs.sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
}

/**
 * Hook principal pour gérer les jobs Sora avec subscription RTDB
 */
export function useSoraJobs() {
  const { firebaseUserId } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialLoadDone = useRef(false);

  // Subscription RTDB pour les mises à jour temps réel
  useEffect(() => {
    if (!firebaseUserId) {
      setJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const jobsRef = ref(database, `users/${firebaseUserId}/sora_jobs`);

    console.log('[useSoraJobs] Subscribing to RTDB:', `users/${firebaseUserId}/sora_jobs`);

    const handleValue = async (snapshot) => {
      const data = snapshot.val();
      console.log('[useSoraJobs] RTDB update received, jobs count:', data ? Object.keys(data).length : 0);
      
      const parsedJobs = parseJobsFromRTDB(data);
      
      // Si RTDB retourne des données vides mais c'est le premier load,
      // essayer de charger depuis l'API au cas où les données existent côté serveur
      if (parsedJobs.length === 0 && !initialLoadDone.current) {
        console.log('[useSoraJobs] RTDB empty on initial load, trying API fallback...');
        try {
          const apiJobs = await getMyJobs();
          if (apiJobs && apiJobs.length > 0) {
            console.log('[useSoraJobs] API has jobs:', apiJobs.length);
            setJobs(apiJobs);
          } else {
            setJobs([]);
          }
        } catch (apiErr) {
          console.warn('[useSoraJobs] API fallback failed:', apiErr);
          setJobs([]);
        }
      } else {
        setJobs(parsedJobs);
      }
      
      setError(null);
      setLoading(false);
      initialLoadDone.current = true;
    };

    const handleError = async (err) => {
      console.error('[useSoraJobs] RTDB subscription error:', err);
      
      // Fallback to API when RTDB fails (permissions, network, etc.)
      console.log('[useSoraJobs] Falling back to API...');
      try {
        const apiJobs = await getMyJobs();
        console.log('[useSoraJobs] API fallback success, jobs count:', apiJobs?.length || 0);
        setJobs(apiJobs || []);
        setError(null);
      } catch (apiErr) {
        console.error('[useSoraJobs] API fallback also failed:', apiErr);
        setError(err.message);
      }
      setLoading(false);
      initialLoadDone.current = true;
    };

    onValue(jobsRef, handleValue, handleError);

    return () => {
      console.log('[useSoraJobs] Unsubscribing from RTDB');
      off(jobsRef);
    };
  }, [firebaseUserId]);

  // Fonction pour mettre à jour un job localement (optimistic update)
  const upsertJob = useCallback((job) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.job_id === job.job_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...job };
        return next;
      }
      // Nouveau job, l'ajouter en premier
      return [job, ...prev];
    });
  }, []);

  // Fonction pour supprimer un job localement
  const removeJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
  }, []);

  // Force refresh - recharge depuis l'API
  const forceRefresh = useCallback(async () => {
    console.log('[useSoraJobs] Force refresh requested - loading from API...');
    setLoading(true);
    try {
      const apiJobs = await getMyJobs();
      console.log('[useSoraJobs] Force refresh success, jobs count:', apiJobs?.length || 0);
      setJobs(apiJobs || []);
      setError(null);
    } catch (err) {
      console.error('[useSoraJobs] Force refresh failed:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  return {
    jobs,
    loading,
    error,
    upsertJob,
    removeJob,
    forceRefresh,
  };
}

export default useSoraJobs;
